import { Flip, type Id, type ToastContainerProps, type ToastOptions, toast } from 'react-toastify';
import {
  isTourStepVisible,
  tourAlertEdge,
  useTourPresenceStore,
} from '../stores/tour-presence.store';

/**
 * THE APP'S TOAST TIMING AND ANNOUNCEMENT, BY TYPE (one place; the 171 call
 * sites stay `toast.error(msg)`). The look is design-system/toasts.css.
 *
 * Timing: long enough to read (WCAG 2.2.1; DARK-MODE.md A10: it was 1 s for
 * everything). Errors and warnings ask the person to do something, so they
 * stay longer. Hover or keyboard focus pauses the clock, a tap dismisses.
 *
 * Announcement: an error interrupts (role="alert", assertive); everything
 * else waits its turn (role="status", polite). react-toastify's default is
 * "alert" for every toast, so a "Saved" cut across whatever a screen reader
 * was saying.
 *
 * react-toastify takes one autoClose and one role for the whole container,
 * so the per-type defaults are merged into each call by wrapping the four
 * typed methods once (installToastTypeDefaults). A call that passes its own
 * autoClose or role keeps it.
 *
 * The tour (David, 2026-10-29): while a guided-tour or coach-mark step is on
 * screen (stores/tour-presence.store.ts, the one answer), toasts keep out of
 * its way, here and not at the call sites:
 *   - success and info are held back and DROPPED, not queued. The tour
 *     narrates what just happened; a "Sentence updated!" that surfaced after
 *     the tour would be about something the learner left a minute ago.
 *   - a success or info toast already up when a step appears is dismissed
 *     (e.g. a sign-up's welcome under the tour's first bubble).
 *   - warning and error still show, at the edge the step leaves free (the
 *     bottom, above the bottom nav and the safe area: design-system/toasts.css;
 *     the top for the camera coach mark, whose bubble is at the bottom), and
 *     one at a time, so a stack never climbs into the bubble.
 * Outside a tour nothing changes.
 */
export const TOAST_AUTO_CLOSE_MS = {
  info: 4000,
  success: 4000,
  warning: 6000,
  error: 6000,
} as const;

type ToastType = keyof typeof TOAST_AUTO_CLOSE_MS;

export const TOAST_ROLE: Record<ToastType, 'alert' | 'status'> = {
  info: 'status',
  success: 'status',
  warning: 'status',
  error: 'alert',
};

export const TOAST_LIMIT = 3;

/** The container: position, motion and the defaults for an untyped `toast()`. */
export const TOAST_CONTAINER_PROPS = {
  position: 'top-center',
  transition: Flip,
  autoClose: TOAST_AUTO_CLOSE_MS.info,
  role: TOAST_ROLE.info,
  limit: TOAST_LIMIT,
  closeButton: false,
  closeOnClick: true,
  pauseOnHover: true,
  pauseOnFocusLoss: true,
  theme: 'light',
} satisfies ToastContainerProps;

type Typed = (
  content: Parameters<typeof toast.info>[0],
  options?: ToastOptions,
) => ReturnType<typeof toast.info>;

const INSTALLED = Symbol.for('empathease.toastTypeDefaults');

/** The call's options over the type's defaults; an option set to undefined does not erase a default. */
export function withTypeDefaults(type: ToastType, options?: ToastOptions): ToastOptions {
  const own = Object.fromEntries(
    Object.entries(options ?? {}).filter(([, v]) => v !== undefined),
  ) as ToastOptions;
  return { autoClose: TOAST_AUTO_CLOSE_MS[type], role: TOAST_ROLE[type], ...own };
}

/** success/info: what a tour step holds back. */
const QUIET_TYPES: ReadonlySet<ToastType> = new Set<ToastType>(['success', 'info']);
/** How many recent success/info ids are kept to dismiss when a step appears (more than can be up or queued). */
const RECENT_QUIET_KEPT = 12;

type TourPresence = Pick<typeof useTourPresenceStore, 'getState' | 'subscribe'>;

/**
 * Wraps toast.info/success/warning/error (and the toast.warn alias) so each
 * gets its type's autoClose and role, and keeps out of a tour step's way
 * (above). Idempotent: a second call (HMR, a test importing twice) does not
 * wrap again.
 */
export function installToastTypeDefaults(
  target: typeof toast = toast,
  presence: TourPresence = useTourPresenceStore,
): void {
  const marked = target as typeof toast & { [INSTALLED]?: true };
  if (marked[INSTALLED]) return;

  // The success/info toasts recently shown, newest last: the ones a step dismisses.
  const recentQuiet: Id[] = [];
  // The alert shown during the current tour, so the next one replaces it.
  let tourAlert: Id | null = null;
  let held = 0;

  for (const type of Object.keys(TOAST_AUTO_CLOSE_MS) as ToastType[]) {
    const original = target[type] as Typed;
    if (typeof original !== 'function') continue;
    const wrapped: Typed = (content, options) => {
      const edge = tourAlertEdge(presence.getState());
      const merged = withTypeDefaults(type, options);
      if (edge === null) {
        const id = original(content, merged);
        if (QUIET_TYPES.has(type)) {
          recentQuiet.push(id);
          if (recentQuiet.length > RECENT_QUIET_KEPT) recentQuiet.shift();
        }
        return id;
      }
      // A tour step is up. An id nobody shows: dismiss/update/isActive on it are no-ops.
      if (QUIET_TYPES.has(type)) return `tour-held-${++held}`;
      if (tourAlert !== null && tourAlert !== merged.toastId) target.dismiss(tourAlert);
      const id = original(content, {
        ...merged,
        position: edge === 'top' ? 'top-center' : 'bottom-center',
      });
      tourAlert = id;
      return id;
    };
    (target as unknown as Record<ToastType, Typed>)[type] = wrapped;
  }
  // toast.warn is react-toastify's alias of toast.warning; keep it one.
  (target as unknown as { warn: Typed }).warn = target.warning as Typed;

  // A step appearing clears the success/info toasts still up; a tour ending
  // forgets its alert (the next tour starts its own one-at-a-time); the edge
  // changing dismisses the alert that is up, which sits where the step now
  // is (b-172: a bottom alert over the Giraffe dialog's bottom bar).
  presence.subscribe((state, prev) => {
    const now = isTourStepVisible(state);
    const before = isTourStepVisible(prev);
    if (now && !before) {
      for (const id of recentQuiet.splice(0)) target.dismiss(id);
    } else if (!now && before) {
      tourAlert = null;
    } else if (now && tourAlertEdge(state) !== tourAlertEdge(prev) && tourAlert !== null) {
      target.dismiss(tourAlert);
      tourAlert = null;
    }
  });
  marked[INSTALLED] = true;
}
